
import ConfirmHoanThanh from "@/components/shared/confirm-hoan-thanh-modal/ConfirmHoanThanh.vue";
import ConfirmationApproveDialog from "@/components/confirmation-approve-dialog/ConfirmationApproveDialog.vue";
import Setting from "@/components/shared/setting/Setting.vue";
import type { SettingAction } from "@/components/shared/setting/Setting.vue";
import WordBreakColumn from "@/components/shared/work-break-column/WordBreakColumn.vue";
import { useBienBan, shouldShowConfirmButtonForLevel, shouldShowApproveButtonForLevel } from "@/core/hooks/use-bien-ban-hook";
import { useBienBanComplete } from "@/core/hooks/use-bien-ban-complete";
import { useBienBanConfirmApprove } from "@/core/hooks/use-bien-ban-confirm-approve";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { usePagination } from "@/core/hooks/use-pagination";
import { ButtonsType } from "@/core/enums/buttons.enums";
import { IBienBanGiaiTheResponseBase } from "@/core/interfaces/ApiResponses";
import { defineComponent, PropType } from "vue";
import BaseLink from "@/components/shared/base-link/BaseLink.vue";
import IconCircleCheckClassicRegular from "@/icons/IconCircleCheckClassicRegular.vue";
import IconEye from "@/icons/IconEye.vue";
import IconMemoCircleCheckClassicSolid from "@/icons/IconMemoCircleCheckClassicSolid.vue";
import IconPencil from "@/icons/IconPencil.vue";
import IconPrint from "@/icons/IconPrint.vue";
import IconTrash from "@/icons/IconTrash.vue";
import { openBienBanGiaiThePdf } from "@/components/shared/pdf-viewer/open-bien-ban-giai-the-pdf";

export default defineComponent({
  name: "bien-ban-giai-the-table",

  components: {
    BaseLink,
    ConfirmHoanThanh,
    ConfirmationApproveDialog,
    Setting,
    WordBreakColumn,
  },

  emits: [
    "on-check-click",
    "on-detail-click",
    "on-edit-click",
    "on-delete-click",
    "on-refresh",
  ],

  props: {
    curPage: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 0,
    },
    items: {
      type: Array as PropType<Array<IBienBanGiaiTheResponseBase>>,
      default: () => [],
    },
    isKeHoachColumnDisplaying: {
      type: Boolean,
      default: true,
    },
    isHoanThanhButtonDisplayed: {
      type: Boolean,
      default: true,
    },
    showApprovalButtons: {
      type: Boolean,
      default: true,
    },
    showDeleteButton: {
      type: Boolean,
      default: false,
    },
    showEditButton: {
      type: Boolean,
      default: true,
    },
    showCapSuaChuaColumn: {
      type: Boolean,
      default: false,
    },
    showCompleteButton: {
      type: Boolean,
      default: true,
    },
    showConfirmButton: {
      type: Boolean,
      default: true,
    },
    showNhomToaXeColumn: {
      type: Boolean,
      default: false,
    },
    onSuccess: {
      type: Function as PropType<() => void | Promise<void>>,
      default: undefined,
    },
    /**
     * Custom action buttons array. If provided, these buttons will be used instead of default buttons.
     * Each button object should contain:
     * - buttonType: string - The button type from ButtonsType enum
     * - colorType: string - The color type from ButtonTypeColors enum
     * - tooltipMessage: string - Tooltip text for the button
     * - condition?: (row: any) => boolean - Optional condition function to show/hide button
     * - onClick: (row: any) => void - Click handler function
     * - icon?: string - Optional icon identifier
     */
    actionButtons: {
      type: Array as PropType<
        Array<{
          buttonType: string;
          colorType: string;
          tooltipMessage: string;
          condition?: (row: IBienBanGiaiTheResponseBase) => boolean;
          onClick: (row: IBienBanGiaiTheResponseBase) => void;
          icon?: string;
        }>
      >,
      default: () => [],
    },
  },
  data() {
    return {
      bienBanItems: this.items as IBienBanGiaiTheResponseBase[],
      toaXeHasDmtx(toaxe): boolean {
        return toaxe.danh_muc_toa_xe?.length > 0;
      },
    };
  },

  watch: {
    items(value) {
      this.bienBanItems = value;
    },
  },

  setup(props, { emit }) {
    const { moment } = useDateTime();
    const { indexCal } = usePagination();
    const { approvalLevel } = useBienBan();

    // Handler for success callback - either use provided callback or emit refresh event
    const handleSuccess = async () => {
      if (props.onSuccess) {
        await props.onSuccess();
      } else {
        emit("on-refresh");
      }
    };

    const {
      showConfirmDialog,
      showApproveDialog,
      currentBienBan: currentApprovalBienBan,
      openConfirmDialog,
      openApproveDialog,
      closeConfirmDialog,
      closeApproveDialog,
      handleConfirm,
      handleApprove,
    } = useBienBanConfirmApprove(handleSuccess);

    const {
      showCompleteDialog,
      currentBienBan: currentCompleteBienBan,
      openCompleteDialog,
      closeCompleteDialog,
      handleComplete,
    } = useBienBanComplete(handleSuccess);

    return {
      moment,
      indexCal,
      approvalLevel,
      shouldShowConfirmButtonForLevel,
      shouldShowApproveButtonForLevel,
      handleSuccess,
      showConfirmDialog,
      showApproveDialog,
      currentApprovalBienBan,
      openConfirmDialog,
      openApproveDialog,
      closeConfirmDialog,
      closeApproveDialog,
      handleConfirm,
      handleApprove,
      showCompleteDialog,
      currentCompleteBienBan,
      openCompleteDialog,
      closeCompleteDialog,
      handleComplete,
    };
  },

  computed: {
    /**
     * Determines whether to show default buttons or custom action buttons
     */
    shouldShowDefaultButtons(): boolean {
      return this.actionButtons.length === 0;
    },
  },
  methods: {
    getSettingActions(row: IBienBanGiaiTheResponseBase): SettingAction[] {
      const actions: SettingAction[] = [
        {
          icon: IconEye,
          label: "Chi tiết BBGT",
          tooltip: "Chi tiết BBGT",
          // all: ['xem-bbgt'],
          onClick: () => this.$emit("on-detail-click", { id: row.id }),
        },
        {
          icon: IconPrint,
          label: "In Biên bản giải thể",
          tooltip: "In Biên bản giải thể",
          onClick: () => openBienBanGiaiThePdf(this.$router, row.id),
        },
      ];

      this.getVisibleActionButtons(row).forEach((button) => {
        actions.push({
          icon: this.getIconForButtonType(button.buttonType),
          label: button.tooltipMessage,
          tooltip: button.tooltipMessage,
          onClick: () => button.onClick(row),
        });
      });

      if (!this.shouldShowDefaultButtons) {
        return actions;
      }

      if (this.showEditButton && !row.ngay_ra_xuong && row.can_edit) {
        actions.push({
          icon: IconPencil,
          label: "Sửa BBGT",
          tooltip: "Sửa BBGT",
          any: ["data:bbgt:sua"],
          onClick: () => this.$emit("on-edit-click", { id: row.id }),
        });
      }

      if (
        this.showCompleteButton &&
        this.isHoanThanhButtonDisplayed &&
        !row.ngay_ra_xuong &&
        row.is_admin_approved &&
        row.is_approved
      ) {
        actions.push({
          icon: IconCircleCheckClassicRegular,
          label: "Hoàn thành BBGT",
          tooltip: "Hoàn thành BBGT",
          all: ["data:bbgt:duyet"],
          onClick: () => this.openCompleteDialog(row),
        });
      }

      if (
        this.showConfirmButton &&
        this.showApprovalButtons &&
        this.shouldShowConfirmButtonForLevel(row, this.approvalLevel)
      ) {
        actions.push({
          icon: IconMemoCircleCheckClassicSolid,
          label: "Kỹ thuật xác nhận",
          tooltip: "Kỹ thuật xác nhận",
          all: ["data:bbgt:xac-nhan"],
          onClick: () => this.openConfirmDialog(row),
        });
      }

      if (
        this.showApprovalButtons &&
        this.shouldShowApproveButtonForLevel(row, this.approvalLevel)
      ) {
        actions.push({
          icon: IconCircleCheckClassicRegular,
          label: "GĐ phê duyệt",
          tooltip: this.getApproveTooltip(row),
          all: ["data:bbgt:duyet"],
          onClick: () => this.openApproveDialog(row),
        });
      }

      if (this.showDeleteButton && this.canDeleteBienBan(row)) {
        actions.push({
          icon: IconTrash,
          label: "Xóa BBGT",
          tooltip: "Xóa BBGT",
          any: ["data:bbgt:xoa"],
          onClick: () =>
            this.$emit("on-delete-click", { id: row.id, item: row }),
        });
      }

      return actions;
    },

    getApproveTooltip(row: IBienBanGiaiTheResponseBase): string {
      if (row.is_admin_approved) {
        return "Đã duyệt";
      }

      if (this.approvalLevel === 2 && !row.is_approved) {
        return "Phải xác nhận trước khi duyệt";
      }

      return "GĐ phê duyệt";
    },

    getIconForButtonType(buttonType: string) {
      switch (buttonType) {
        case ButtonsType.EDIT:
          return IconPencil;
        case ButtonsType.TRASH:
          return IconTrash;
        case ButtonsType.PRINT:
          return IconPrint;
        case ButtonsType.APPROVE:
        case ButtonsType.CONFIRM:
        case ButtonsType.DONE:
        case ButtonsType.CIRCLE_CHECK_CLASSIC_REGULAR:
          return IconCircleCheckClassicRegular;
        case ButtonsType.MEMO_CIRCLE_CHECK_CLASSIC_LIGHT:
        case ButtonsType.MEMO_CIRCLE_CHECK_CLASSIC_SOLID:
          return IconMemoCircleCheckClassicSolid;
        case ButtonsType.INFO:
        case ButtonsType.EYE:
        case ButtonsType.DETAIL:
        default:
          return IconEye;
      }
    },

    canDeleteBienBan(bienBan: IBienBanGiaiTheResponseBase): boolean {
      // Can delete if:
      // 1. Not approved and admin approved
      // 2. Has phieu_xuat_kho
      return (
        !(bienBan.is_approved && bienBan.is_admin_approved) ||
        bienBan.phieu_xuat_kho_count === 0
      );
    },
    getVisibleActionButtons(row: IBienBanGiaiTheResponseBase) {
      return this.actionButtons.filter(
        (button) => !button.condition || button.condition(row)
      );
    },
  },
});
